import React, { Fragment, FunctionComponent } from 'react';
import { connect } from 'react-redux';
import ModalDialog from 'shared/modal/dialog';
import { getSortedTimeZoneLabels } from 'shared/timeZoneUtils/timeZone.utils';
import { FilterModel } from 'shared/commonGrid/common-grid.types';
import { adaptToServerValueFilterModel } from 'shared/commonGrid/filterModel.adapter';
import { showError } from 'shared/toaster';
import moment from 'moment';
import ExportReportDialogBody, { ExportReportDialogBodyFormRef } from 'routes/AccessFunction/containers/AccessFunctionList/components/DialogBody/ExportReportDialogBody';
import i18n from 'i18n';
import WiretapGridColumnDefs from './grid-column-defs';
import CommonGrid, { ICommonGridProperties } from '../../../shared/commonGrid';
import DateTimeCellRenderer from '../../../shared/commonGrid/date-cell-renderer';
import { ROWSELECTION } from '../../../shared/commonGrid/grid-enums';
import DateTimeWithTimezoneCellRenderer from '../../../shared/commonGrid/DateTimeWithTimezoneCellRenderer';
import { TimeZoneCellRenderer } from '../../../shared/commonGrid/TimeZoneCellRenderer';
import exportWiretapsToFile from '../utils';

type WiretapGridState = {
  exportReportDialog: {
    isOpen: boolean
    title: string
    customComponent: FunctionComponent
    onSubmit: CallableFunction
    actionText: string
    onClose: CallableFunction
  },
}

class WiretapGrid extends React.Component<any, any> {
  columnDefs = WiretapGridColumnDefs;
  filterCriteria = '';
  exportReportDialogRef: ExportReportDialogBodyFormRef | null = null;

  state: WiretapGridState = {
    exportReportDialog: {
      isOpen: false,
      title: '',
      customComponent: () => null,
      onSubmit: () => null,
      actionText: '',
      onClose: () => null,
    }
  }

  getDataPath = (data: any) => {
    data.cases.map((item: any) => item.name);
  };

  initializeColumnDef = () => {
    const {
      timezones,
    } = this.props;

    const timeZoneCol = this.columnDefs.find((columnDef) => (columnDef.field === 'reporting.timeZone'));
    if (timeZoneCol) {
      timeZoneCol.filter = 'agSetColumnFilter';
      timeZoneCol.filterParams = {
        values: getSortedTimeZoneLabels(timezones),
      };
    }
  }

  componentDidUpdate() {
    this.initializeColumnDef();
  }

  handleSelection = (e: any) => {
    this.setState({ selection: e.api.getSelectedRows() });
  };

  handleGetResultsFromServer = (body: any) => {
    this.filterCriteria = body;
    return this.props.getAllWiretaps(this.props.global.selectedDF, body);
  }

  handleGetGridSettingsFromServer = () => {
    return this.props.getWiretapGridSettings(this.props.authentication.userId);
  }

  saveGridSettings = (wiretapGridSettings: { [key: string]: any }) => {
    if (wiretapGridSettings !== null) {
      this.props.putWiretapGridSettings(this.props.authentication.userId,
        wiretapGridSettings);
    }
  }

  adaptToGridFilterModel = (filterModel:FilterModel): FilterModel => {
    return adaptToServerValueFilterModel(filterModel, 'reporting.timeZone', this.props.timezones);
  }

  onHandleExport = () => {
    const exportReportDialog = {
      isOpen: true,
      title: i18n.t('wiretap.exportPopup.title'),
      customComponent: () => (
        <ExportReportDialogBody
          ref={(ref: any) => {
            this.exportReportDialogRef = ref;
          }}
          exportOption={{
            supportedFileFormats: ['csv'],
            fileFormat: 'csv',
            filename: `wiretaps_export_${moment().valueOf()}`,
            isExporting: false
          }}
        >
          {i18n.t('wiretap.exportPopup.message')}
        </ExportReportDialogBody>
      ),
      actionText: i18n.t('wiretap.exportPopup.action'),
      onSubmit: this.onReportSubmit,
      onClose: () => {
        this.setState({
          exportReportDialog: {
            ...this.state.exportReportDialog,
            isOpen: false
          }
        });
      }
    };

    this.setState({ exportReportDialog });
  };

  onReportSubmit = () => {
    if (this.exportReportDialogRef) {
      exportWiretapsToFile(this.filterCriteria, this.exportReportDialogRef.getExportOption()).then((isSuccessful) => {
        if (!isSuccessful) {
          showError('Failed to export wiretaps to file.');
        }
      });
    }
  };

  render() {
    const { exportReportDialog } = this.state;
    const components = {
      datetimeCellRenderer: DateTimeCellRenderer,
      DateTimeWithTimezoneCellRenderer,
      TimeZoneCellRenderer,
    };
    const commonGridProps: ICommonGridProperties = {
      getDataPath: this.getDataPath,
      onSelectionChanged: this.handleSelection,
      rowSelection: ROWSELECTION.SINGLE,
      columnDefs: this.columnDefs,
      rowData: this.props.wiretaps.wiretaps,
      enableBrowserTooltips: true,
      context: this,
      pagination: true,
      noResults: false,
      components,
      getGridResultsFromServer: this.handleGetResultsFromServer,
      adaptToFilterModel: this.adaptToGridFilterModel,
      renderDataFromServer: true,
      getGridSettingsFromServer: this.handleGetGridSettingsFromServer,
      saveGridSettings: this.saveGridSettings,
      isBeforeLogout: this.props.authentication.beforeLogout,
      onExport: this.onHandleExport

    };
    return (
      <Fragment>
        <div className="wiretap-grid-container" style={{ height: 'inherit' }}>
          <CommonGrid {...commonGridProps} />
        </div>
        <ModalDialog
          width="500px"
          isOpen={exportReportDialog.isOpen}
          title={exportReportDialog.title}
          customComponent={exportReportDialog.customComponent}
          actionText={exportReportDialog.actionText}
          onSubmit={exportReportDialog.onSubmit}
          onClose={exportReportDialog.onClose}
        />
      </Fragment>
    );
  }
}

const mapStateToProps = ({
  users: {
    timezones,
  },
}: any) => ({
  timezones,
});

export default connect(mapStateToProps, null, null, { forwardRef: true })(WiretapGrid);
